import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { DEFAULT_SEO_TITLE } from '@/lib/seo';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertTriangle,
  Ban,
  Search,
  RefreshCw,
  Plane,
  Calendar,
  CreditCard,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  User as UserIcon,
  Mail,
  MailCheck,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { User } from '@/api/entities';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { ErrorBoundary } from '@/lib/errorLogger';
import VipBadge from '@/components/admin/VipBadge';

// Hard ceiling on the fetch. The endpoint walks every active DC/DZ sub through
// Stripe (3 API calls each) so a single page load can legitimately take 60-90s
// at the current booking volume. We still want a finite timeout so a stuck
// Vercel invocation (which silently exceeds the 300s function ceiling) surfaces
// to the admin as a real error instead of leaving the page on "Loading…"
// forever — that empty-loading state is what gets reported as "the page is blank".
const PAST_DUE_FETCH_TIMEOUT_MS = 4 * 60 * 1000;

const fmtCurrency = (cents) => {
  const n = Number(cents) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(n / 100);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return '—';
  }
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'MMM d, yyyy · h:mm a');
  } catch {
    return '—';
  }
};

const intervalLabel = (interval, count) => {
  if (!interval) return null;
  const c = Number(count) || 1;
  const base = interval === 'day' ? 'day' : interval === 'week' ? 'week' : interval === 'month' ? 'month' : interval;
  if (c === 1) {
    if (interval === 'week') return 'Weekly';
    if (interval === 'month') return 'Monthly';
    if (interval === 'day') return 'Daily';
    return base;
  }
  if (interval === 'week' && c === 2) return 'Biweekly';
  return `Every ${c} ${base}s`;
};

const ProductBadge = ({ product }) => {
  if (product === 'defera_capital') {
    return (
      <span className="text-[10px] font-medium text-brand-200/90 bg-brand-950/50 border border-brand-400/25 px-1.5 py-0.5 rounded">
        Capital
      </span>
    );
  }
  if (product === 'defera_zero') {
    return (
      <span className="text-[10px] font-medium text-emerald-200/90 bg-emerald-950/50 border border-emerald-400/25 px-1.5 py-0.5 rounded">
        Zero
      </span>
    );
  }
  return null;
};

const ProcessorBadge = ({ processor }) => {
  if (processor === 'nmi') {
    return (
      <span className="text-[10px] font-medium text-violet-200/90 bg-violet-950/50 border border-violet-400/25 px-1.5 py-0.5 rounded">
        NMI
      </span>
    );
  }
  return null;
};

const SubStatusBadge = ({ status }) => {
  const map = {
    past_due: 'text-rose-200/90 bg-rose-950/50 border-rose-400/30',
    active: 'text-emerald-200/90 bg-emerald-950/50 border-emerald-400/25',
    canceled: 'text-zinc-300 bg-zinc-900/60 border-zinc-500/25',
    unpaid: 'text-brand-200/90 bg-brand-950/50 border-brand-400/25',
    trialing: 'text-brand-200/90 bg-brand-950/50 border-brand-400/25',
    incomplete: 'text-brand-200/90 bg-brand-950/50 border-brand-400/25',
  };
  const cls = map[status] || 'text-white/70 bg-white/5 border-white/15';
  return (
    <span className={`text-[10px] uppercase tracking-wider font-medium border px-1.5 py-0.5 rounded ${cls}`}>
      {status}
    </span>
  );
};

const TicketBadge = ({ booked }) => (
  <span
    className={`inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded ${
      booked
        ? 'text-emerald-200/90 bg-emerald-950/40 border-emerald-400/25'
        : 'text-brand-200/90 bg-brand-950/40 border-brand-400/25'
    }`}
  >
    {booked ? <CheckCircle className="w-2.5 h-2.5" /> : <XCircle className="w-2.5 h-2.5" />}
    {booked ? 'Ticketed' : 'Not booked'}
  </span>
);

const DaysOverdueBadge = ({ days }) => {
  if (days == null) return null;
  const cls =
    days >= 14
      ? 'text-rose-100 bg-rose-900/60 border-rose-400/40'
      : days >= 7
        ? 'text-rose-200 bg-rose-950/50 border-rose-400/30'
        : 'text-brand-200 bg-brand-950/50 border-brand-400/30';
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded ${cls}`}>
      <Clock className="w-2.5 h-2.5" />
      {days === 0 ? 'Due today' : `${days}d overdue`}
    </span>
  );
};

// "Payments behind" = number of open Stripe invoices still owed. One open
// invoice = one missed scheduled payment. Severity escalates >=2 / >=3.
const PaymentsBehindBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  const cls =
    count >= 3
      ? 'text-rose-100 bg-rose-900/60 border-rose-400/40'
      : count >= 2
        ? 'text-rose-200 bg-rose-950/50 border-rose-400/30'
        : 'text-brand-200 bg-brand-950/50 border-brand-400/30';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded ${cls}`}
      title={`${count} unpaid invoice${count === 1 ? '' : 's'} on this subscription`}
    >
      <CreditCard className="w-2.5 h-2.5" />
      {count} payment{count === 1 ? '' : 's'} behind
    </span>
  );
};

// "Retries attempted" = sum of `attempt_count` across the open invoices.
// Stripe increments this every time it tries to auto-charge the card and the
// charge fails (Smart Retries / dunning). >=4 lights up rose since
// subscriptions normally cancel after the configured retry ceiling.
const RetriesBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  const cls =
    count >= 4
      ? 'text-rose-200 bg-rose-950/50 border-rose-400/30'
      : 'text-brand-200/90 bg-brand-950/40 border-brand-400/25';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded ${cls}`}
      title="Total Stripe auto-charge retries across all open invoices on this subscription"
    >
      <RefreshCw className="w-2.5 h-2.5" />
      {count} retr{count === 1 ? 'y' : 'ies'} attempted
    </span>
  );
};

/**
 * Automated past-due escalation stage (cron-sent Resend emails + terminal
 * status flip). Shows where the booking sits on the ladder so ops can judge
 * what's already been done for them:
 *
 *   default-warning sent → default-final-notice sent →
 *   status='defaulted' (auto-flipped 7 days after the final notice).
 *
 * The auto-default step also opens a collections case via the
 * bookings.status->'defaulted' trigger, so once this chip flips to
 * "Defaulted" there is nothing left for ops to do here — the customer is
 * already in collections.
 */
const EscalationBadge = ({ row }) => {
  if (row.default_auto_defaulted_at) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded text-rose-100 bg-rose-900/60 border-rose-400/40"
        title={`Cron auto-flipped booking to status='defaulted' on ${fmtDate(row.default_auto_defaulted_at)} — 7 days after default-final-notice (${fmtDate(row.default_final_notice_sent_at)}). Collections case was opened automatically.`}
      >
        <Ban className="w-2.5 h-2.5" />
        Defaulted {fmtDate(row.default_auto_defaulted_at)}
      </span>
    );
  }
  if (row.default_final_notice_sent_at) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded text-rose-200/90 bg-rose-950/50 border-rose-400/30"
        title={`default-final-notice sent ${fmtDate(row.default_final_notice_sent_at)} (warning ${fmtDate(row.default_warning_sent_at)}) — booking auto-flips to Defaulted 7 days after the final notice if still 2+ behind.`}
      >
        <MailCheck className="w-2.5 h-2.5" />
        Final notice {fmtDate(row.default_final_notice_sent_at)}
      </span>
    );
  }
  if (row.default_warning_sent_at) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded text-amber-200/90 bg-amber-950/50 border-amber-400/25"
        title={`default-warning sent ${fmtDate(row.default_warning_sent_at)} — final notice follows automatically if still 2+ behind after the deadline`}
      >
        <MailCheck className="w-2.5 h-2.5" />
        Warning {fmtDate(row.default_warning_sent_at)}
      </span>
    );
  }
  return null;
};

const DepartureBadge = ({ iso }) => {
  if (!iso) return null;
  try {
    const dep = parseISO(iso);
    const diffMs = dep.getTime() - Date.now();
    const days = Math.floor(diffMs / 86400000);
    const inPast = diffMs < 0;
    const cls = inPast
      ? 'text-zinc-300 bg-zinc-900/60 border-zinc-500/25'
      : days <= 7
        ? 'text-rose-200/90 bg-rose-950/50 border-rose-400/30'
        : days <= 30
          ? 'text-brand-200/90 bg-brand-950/50 border-brand-400/30'
          : 'text-white/70 bg-white/5 border-white/15';
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded ${cls}`}>
        <Calendar className="w-2.5 h-2.5" />
        {fmtDate(iso)}
        {!inPast && <span className="opacity-70">· in {days}d</span>}
        {inPast && <span className="opacity-70">· flown</span>}
      </span>
    );
  } catch {
    return null;
  }
};

// When the endpoint returns `bookings: []`, this panel breaks the silence by
// dumping the server's counters: how many bookings matched the DB query, how
// many had a Stripe sub_id, how many Stripe lookups failed, and what statuses
// Stripe actually returned. With these numbers an empty page becomes
// self-diagnosing — admin can immediately see e.g. "87 candidates, 87
// resource_missing from Stripe" (env key mismatch) vs "87 candidates, 0
// past_due" (everyone is actually current).
const DiagnosticsPanel = ({ d }) => {
  const totalDb = (d.bookings_db_dc1 || 0) + (d.bookings_db_dc2 || 0)
    + (d.bookings_db_dz1 || 0) + (d.bookings_db_dz2 || 0);
  const subStatuses = d.stripe_status_counts || {};
  const subStatusEntries = Object.entries(subStatuses).sort((a, b) => b[1] - a[1]);
  const row = (label, value) => (
    <div className="flex justify-between gap-4 text-[11px] font-normal tabular-nums">
      <span className="text-white/40">{label}</span>
      <span className="text-white/80">{value}</span>
    </div>
  );
  return (
    <div className="mx-auto max-w-md text-left rounded-xl border border-white/10 bg-black/30 px-4 py-3 space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-white/40 font-normal pb-1.5 border-b border-white/10 mb-1.5">
        Diagnostics (server-side)
      </div>
      {row('Bookings in DB (raw, w/ dupes)', totalDb)}
      {row('After dedup', d.bookings_dedup ?? '—')}
      {row('With Stripe sub_id', d.bookings_with_sub ?? '—')}
      {row('Stripe fetch errors', d.stripe_fetch_errors ?? 0)}
      {row('Stripe "resource_missing"', d.stripe_resource_missing ?? 0)}
      {row('Open-invoice fetch errors', d.open_invoice_fetch_errors ?? 0)}
      {row('Stripe key mode', d.stripe_key_mode ?? '—')}
      {row('Stripe key suffix (Vercel)', d.stripe_key_suffix ? `…${d.stripe_key_suffix}` : '—')}
      {row('Stripe key length', d.stripe_key_length ?? '—')}
      {row('Stripe scan skipped', d.stripe_skipped ? 'yes' : 'no')}
      {row('Filtered out (not past-due)', d.filtered_out_not_past_due ?? 0)}
      {row('Past-due via sub.status', d.past_due_via_sub_status ?? 0)}
      {row('Past-due via failed invoice attempt', d.past_due_via_failed_attempt ?? 0)}
      {row('Past-due via overdue invoice', d.past_due_via_overdue_invoice ?? 0)}
      {row('Past-due via NMI installments', d.past_due_via_nmi ?? 0)}
      {(d.stripe_error_samples || []).length > 0 && (
        <div className="pt-2 mt-2 border-t border-white/10 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-rose-300/80 font-normal">
            Stripe error samples
          </div>
          {(d.stripe_error_samples || []).map((sample) => (
            <div key={`${sample.code}-${sample.sub_id_prefix}-${sample.message}`} className="text-[11px] font-normal text-rose-200/90">
              <span className="text-white/50">{sample.code}</span>
              {sample.sub_id_prefix ? ` · ${sample.sub_id_prefix}…` : ''}
              {' — '}
              {sample.message}
            </div>
          ))}
        </div>
      )}
      {subStatusEntries.length > 0 && (
        <div className="pt-2 mt-2 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-wider text-white/40 font-normal mb-1">
            Stripe sub statuses returned
          </div>
          {subStatusEntries.map(([status, count]) => (
            <div
              key={status}
              className="flex justify-between gap-4 text-[11px] font-normal tabular-nums"
            >
              <span className="text-white/40">{status}</span>
              <span className="text-white/80">{count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Public wrapper: the actual component is rendered inside an ErrorBoundary so a
// silent render crash (e.g. unexpected data shape from /api/admin/past-due-bookings)
// surfaces as a visible "Something went wrong" panel + logs to the error_logs
// table, instead of leaving the admin staring at a blank black main area while
// the sidebar still renders. That blank-with-sidebar pattern is exactly what
// gets reported as "the Past Due page doesn't work" — and without a boundary
// we have no signal at all when it happens.
export default function PastDue() {
  return (
    <ErrorBoundary componentName="PastDue">
      <PastDueInner />
    </ErrorBoundary>
  );
}

function PastDueInner() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    count: 0,
    total_past_due_cents: 0,
    total_remaining_cents: 0,
    not_ticketed_count: 0,
    generated_at: null,
  });
  // Server-side counters from /api/admin/past-due-bookings. Surfaced in the
  // empty state so when the list comes back [] we can tell at a glance
  // whether the cause is "no candidate bookings", "Stripe doesn't know any
  // of the sub IDs" (env/key mismatch), or "every sub returned a non-past-due
  // status". Without this an empty page is indistinguishable from a bug.
  const [diagnostics, setDiagnostics] = useState(null);
  const [loadWarnings, setLoadWarnings] = useState([]);
  // "Defaulted profit" tile: cash actually collected (deposit + installments
  // + one-time payments) on trips whose status later became 'defaulted'.
  // Loaded independently of the main scan above — defaulted trips whose subs
  // Stripe already canceled are filtered OUT of /api/admin/past-due-bookings
  // rows, so this tile can't be derived from `rows`. Instead we fetch the
  // defaulted booking IDs and hand them to /api/admin/bookings-paid-amounts,
  // which owns the unified Stripe + NMI paid-cents math and its cache.
  const [defaultedProfit, setDefaultedProfit] = useState({ state: 'loading', cents: 0, count: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [ticketFilter, setTicketFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const canAccess =
    !!currentUser && (currentUser.role === 'admin' || currentUser.app_metadata?.roles?.includes('admin'));

  const load = async ({ force = false } = {}) => {
    setIsRefreshing(true);
    setErrorMsg('');
    const startedAt = Date.now();
    // Abort the fetch ourselves so a hung Vercel invocation surfaces as a
    // readable error instead of leaving isLoading=true forever. Without this
    // the page renders the (subtle) "Loading…" branch indefinitely, which is
    // visually indistinguishable from a blank page on a dark screen.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PAST_DUE_FETCH_TIMEOUT_MS);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      console.log('[PastDue] requesting /api/admin/past-due-bookings', { force });
      const resp = await fetch('/api/admin/past-due-bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ refresh: !!force }),
        credentials: 'omit',
        signal: controller.signal,
      });

      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || `HTTP ${resp.status}`);
      }

      const data = await resp.json();
      const elapsed = Math.round((Date.now() - startedAt) / 1000);
      console.log(
        `[PastDue] loaded ${Array.isArray(data.bookings) ? data.bookings.length : 0} bookings in ${elapsed}s`,
        { cached: data.cached, generated_at: data.generated_at, diagnostics: data.diagnostics },
      );
      setRows(Array.isArray(data.bookings) ? data.bookings : []);
      setSummary({
        count: data.count || 0,
        total_past_due_cents: data.total_past_due_cents || 0,
        total_remaining_cents: data.total_remaining_cents || 0,
        not_ticketed_count: data.not_ticketed_count || 0,
        generated_at: data.generated_at || null,
      });
      setDiagnostics(data.diagnostics || null);
      setLoadWarnings(Array.isArray(data.warnings) ? data.warnings : []);
    } catch (e) {
      console.error('[PastDue] load failed:', e);
      const isAbort = e?.name === 'AbortError';
      const msg = isAbort
        ? `Request timed out after ${Math.round(PAST_DUE_FETCH_TIMEOUT_MS / 1000)}s. The server may be overloaded — try Refresh.`
        : e.message || 'Failed to load past-due bookings';
      setErrorMsg(msg);
      toast.error(isAbort ? 'Past Due request timed out' : 'Failed to load past-due bookings');
    } finally {
      clearTimeout(timeoutId);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    console.log('[PastDue] mounted, resolving current user…');
    const init = async () => {
      try {
        const me = await User.me();
        console.log('[PastDue] User.me resolved', {
          hasUser: !!me,
          role: me?.role,
          roles: me?.app_metadata?.roles,
          email: me?.email,
        });
        setCurrentUser(me);
        if (me && (me.role === 'admin' || me.app_metadata?.roles?.includes('admin'))) {
          // First page open: let the 60s server-side cache absorb the cost.
          // Refresh button explicitly passes { force: true } to bypass it.
          await load({ force: false });
        } else {
          console.log('[PastDue] not admin — showing access-required screen');
        }
      } catch (e) {
        // Don't swallow — log so we can tell why a logged-in admin would
        // ever drop into the non-admin branch.
        console.warn('[PastDue] init() failed:', e);
      } finally {
        setIsLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!canAccess) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { supabase } = await import('@/lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Not authenticated');
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        };
        const idsResp = await fetch('/api/admin/defaulted-bookings', { method: 'POST', headers });
        if (!idsResp.ok) throw new Error(`HTTP ${idsResp.status}`);
        const idsData = await idsResp.json();
        const ids = Array.isArray(idsData.booking_ids) ? idsData.booking_ids : [];
        if (ids.length === 0) {
          if (!cancelled) setDefaultedProfit({ state: 'loaded', cents: 0, count: 0 });
          return;
        }
        const paidResp = await fetch('/api/admin/bookings-paid-amounts', {
          method: 'POST',
          headers,
          body: JSON.stringify({ booking_ids: ids }),
        });
        if (!paidResp.ok) throw new Error(`HTTP ${paidResp.status}`);
        const paidData = await paidResp.json();
        const cents = Object.values(paidData.paid || {}).reduce(
          (sum, c) => sum + (Number(c) || 0),
          0,
        );
        if (!cancelled) setDefaultedProfit({ state: 'loaded', cents, count: ids.length });
      } catch (e) {
        console.warn('[PastDue] defaulted-profit load failed:', e);
        if (!cancelled) setDefaultedProfit((prev) => ({ ...prev, state: 'error' }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess]);

  const filtered = useMemo(() => {
    const s = String(searchTerm || '').trim().toLowerCase();
    return rows.filter((r) => {
      if (productFilter !== 'all' && r.payment_product !== productFilter) return false;
      if (ticketFilter === 'ticketed' && !r.is_ticketed) return false;
      if (ticketFilter === 'not_ticketed' && r.is_ticketed) return false;
      if (!s) return true;
      const blob = [
        r.user_name,
        r.user_email,
        r.booking_reference,
        r.confirmation_number,
        r.route,
        r.stripe_subscription_id,
        r.stripe_customer_id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(s);
    });
  }, [rows, searchTerm, productFilter, ticketFilter]);

  if (isLoading) {
    // Use the same outer chrome (header padding, max-width) as the loaded view
    // so this state never collapses into something that looks like a blank page.
    // The endpoint walks every active sub through Stripe (3 calls each) and can
    // legitimately take 60-90s on first hit; without a visible spinner + status
    // line the admin assumes it's broken.
    return (
      <div className="max-w-6xl mx-auto px-3 sm:px-6 md:px-8 py-4 sm:py-6 pt-20">
        <div className="flex items-center gap-3 mb-6">
          <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400 shrink-0" />
          <h2 className="text-xl sm:text-2xl font-light text-white">Past Due</h2>
        </div>
        <div className="glass-light rounded-2xl border border-white/10 p-8 flex flex-col items-center justify-center gap-3 min-h-[40vh]">
          <Loader2 className="w-6 h-6 text-white/80 animate-spin" />
          <p className="text-white font-normal">Loading past-due bookings…</p>
          <p className="text-white/60 text-xs font-normal text-center max-w-md">
            Pulling every active Capital + Zero subscription from Stripe.
            This can take up to a minute or two on first load.
          </p>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="max-w-6xl mx-auto px-3 sm:px-6 md:px-8 py-4 sm:py-6 pt-20">
        <div className="flex items-center gap-3 mb-6">
          <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400 shrink-0" />
          <h2 className="text-xl sm:text-2xl font-light text-white">Past Due</h2>
        </div>
        <div className="glass-light rounded-2xl border border-white/10 p-8 text-center min-h-[40vh] flex items-center justify-center">
          <p className="text-white/70 font-normal">Administrator access required.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{DEFAULT_SEO_TITLE}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="max-w-6xl mx-auto px-3 sm:px-6 md:px-8 py-4 sm:py-6 pt-20">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 sm:mb-6"
        >
          <h2 className="text-xl sm:text-2xl font-light text-white flex items-center gap-2 sm:gap-3">
            <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400 shrink-0" />
            Past Due
          </h2>
          <p className="text-white/60 text-xs sm:text-sm font-normal mt-1">
            Bookings with past-due installment plans (Defera Capital Stripe + NMI, Defera Zero Stripe).
            {summary.generated_at && (
              <span className="block sm:inline sm:ml-2 text-white/40">
                <span className="hidden sm:inline">· </span>refreshed {fmtDateTime(summary.generated_at)}
              </span>
            )}
          </p>
        </motion.div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="glass-light rounded-xl border border-white/10 p-2.5 sm:p-3 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-white/50 font-normal truncate">Plans past-due</div>
            <div className="text-lg sm:text-xl font-light text-white mt-1 tabular-nums truncate">{summary.count}</div>
          </div>
          <div className="glass-light rounded-xl border border-white/10 p-2.5 sm:p-3 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-white/50 font-normal truncate">Past-due balance</div>
            <div className="text-lg sm:text-xl font-light text-rose-300 mt-1 tabular-nums truncate">
              {fmtCurrency(summary.total_past_due_cents)}
            </div>
          </div>
          <div className="glass-light rounded-xl border border-white/10 p-2.5 sm:p-3 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-white/50 font-normal truncate">Remaining</div>
            <div className="text-lg sm:text-xl font-light text-white mt-1 tabular-nums truncate">
              {fmtCurrency(summary.total_remaining_cents)}
            </div>
          </div>
          <div className="glass-light rounded-xl border border-white/10 p-2.5 sm:p-3 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-white/50 font-normal truncate">Not yet ticketed</div>
            <div className="text-lg sm:text-xl font-light text-brand-300 mt-1 tabular-nums truncate">{summary.not_ticketed_count}</div>
          </div>
          <div
            className="glass-light rounded-xl border border-white/10 p-2.5 sm:p-3 min-w-0 col-span-2 md:col-span-1"
            title="Cash collected (deposits + installments + one-time payments) on trips that later defaulted"
          >
            <div className="text-[10px] uppercase tracking-wider text-white/50 font-normal truncate">Defaulted profit</div>
            <div className="text-lg sm:text-xl font-light text-emerald-300 mt-1 tabular-nums truncate">
              {defaultedProfit.state === 'loading' && (
                <Loader2 className="w-4 h-4 animate-spin text-white/40 inline-block" />
              )}
              {defaultedProfit.state === 'error' && '—'}
              {defaultedProfit.state === 'loaded' && fmtCurrency(defaultedProfit.cents)}
            </div>
            {defaultedProfit.state === 'loaded' && (
              <div className="text-[10px] text-white/40 font-normal mt-0.5 truncate">
                collected across {defaultedProfit.count} defaulted trip{defaultedProfit.count === 1 ? '' : 's'}
              </div>
            )}
          </div>
        </div>

        {/* Filters — stack on mobile so the search field can breathe and the
            product/ticket button groups don't elbow each other into 1-letter
            ellipsized chips. On sm+ everything sits on one row with the
            search greedy-flexing. */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 mb-4">
          <div className="relative w-full sm:flex-1 sm:min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, booking ref, route, sub ID…"
              className="glass text-white border-white/20 pl-10 placeholder:text-white/40 text-sm"
            />
          </div>
          <div className="flex gap-1 w-full sm:w-auto">
            {[
              { id: 'all', label: 'All products', mobileLabel: 'All' },
              { id: 'defera_capital', label: 'Capital', mobileLabel: 'Capital' },
              { id: 'defera_zero', label: 'Zero', mobileLabel: 'Zero' },
            ].map((opt) => (
              <Button
                key={opt.id}
                onClick={() => setProductFilter(opt.id)}
                className={`flex-1 sm:flex-none text-xs font-normal border h-9 px-2 sm:px-3 ${
                  productFilter === opt.id
                    ? 'bg-white/15 text-white border-white/30'
                    : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
                }`}
              >
                <span className="sm:hidden">{opt.mobileLabel}</span>
                <span className="hidden sm:inline">{opt.label}</span>
              </Button>
            ))}
          </div>
          <div className="flex gap-1 w-full sm:w-auto">
            {[
              { id: 'all', label: 'Any' },
              { id: 'not_ticketed', label: 'Not booked' },
              { id: 'ticketed', label: 'Ticketed' },
            ].map((opt) => (
              <Button
                key={opt.id}
                onClick={() => setTicketFilter(opt.id)}
                className={`flex-1 sm:flex-none text-xs font-normal border h-9 px-2 sm:px-3 ${
                  ticketFilter === opt.id
                    ? 'bg-white/15 text-white border-white/30'
                    : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
                }`}
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <Button
            onClick={() => load({ force: true })}
            disabled={isRefreshing}
            className="w-full sm:w-auto h-9 bg-white/10 hover:bg-white/20 text-white border border-white/20 text-xs sm:text-sm"
            title="Refresh from Stripe"
          >
            {isRefreshing ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                Refreshing…
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-1.5" />
                Refresh
              </>
            )}
          </Button>
        </div>

        {errorMsg && (
          <div className="mb-4 rounded-xl border border-rose-400/30 bg-rose-950/40 text-rose-100 text-sm font-normal px-4 py-3">
            {errorMsg}
          </div>
        )}

        {loadWarnings.length > 0 && (
          <div className="mb-4 rounded-xl border border-brand-400/30 bg-brand-950/30 text-brand-100 text-sm font-normal px-4 py-3 space-y-1.5">
            {loadWarnings.map((warning) => (
              <p key={warning}>{warning}</p>
            ))}
          </div>
        )}

        {/* List */}
        <Card className="glass-light border-white/10">
          <CardHeader className="px-3 sm:px-6 pt-3 sm:pt-6 pb-2 sm:pb-3">
            <CardTitle className="text-white font-light text-base sm:text-lg flex items-center gap-2">
              <span>Past-due bookings ({filtered.length})</span>
              {isRefreshing && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/40" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 sm:px-6 pb-4 sm:pb-6">
            {filtered.length === 0 ? (
              <div className="py-6 text-center space-y-3">
                <p className="text-white/60 font-normal">
                  {rows.length === 0
                    ? 'No past-due bookings found.'
                    : 'No bookings match the current filters.'}
                </p>
                {rows.length === 0 && !loadWarnings.length && (
                  <p className="text-white/60 text-xs font-normal max-w-md mx-auto">
                    If you expected results, click Refresh and review the diagnostics below —
                    a Stripe test/live key mismatch is the most common production-only cause.
                  </p>
                )}
                {rows.length === 0 && diagnostics && (
                  <DiagnosticsPanel d={diagnostics} />
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((r) => {
                  const isExpanded = expandedId === r.booking_id;
                  // Compute header-row dunning summary from the already-fetched
                  // open invoices so admins can scan severity without expanding.
                  // - paymentsBehind: prefer the server-side count, fall back to
                  //   the array length for older responses.
                  // - totalRetries: sum of per-invoice attempt_count. We sum
                  //   (rather than show the oldest's max) because retries on
                  //   newer invoices also signal repeated card failure.
                  const openInvoices = Array.isArray(r.open_invoices) ? r.open_invoices : [];
                  const paymentsBehind = r.open_invoice_count != null ? r.open_invoice_count : openInvoices.length;
                  const totalRetries = openInvoices.reduce(
                    (sum, inv) => sum + (Number(inv?.attempt_count) || 0),
                    0,
                  );
                  return (
                    <div
                      key={r.booking_id}
                      className="rounded-2xl border border-white/10 overflow-hidden"
                    >
                      {/* Header row — on mobile the past-due amount drops to its
                          own full-width line so badges + customer refs aren't
                          squeezed against the right rail. On sm+ it stays
                          pinned right, matching the desktop screenshot. */}
                      <div
                        className="px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => setExpandedId(isExpanded ? null : r.booking_id)}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                          <div className="min-w-0 flex-1">
                            {/* Top line: route + product + status */}
                            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap min-w-0">
                              <Plane className="w-3.5 h-3.5 text-white/40 shrink-0" />
                              <span className="text-white font-normal text-sm sm:text-base tracking-tight truncate max-w-full">
                                {r.route || 'N/A'}
                              </span>
                              <ProductBadge product={r.payment_product} />
                              <ProcessorBadge processor={r._payment_processor} />
                              <SubStatusBadge status={r.subscription_status} />
                              <TicketBadge booked={r.is_ticketed} />
                              <DaysOverdueBadge days={r.days_overdue} />
                              <PaymentsBehindBadge count={paymentsBehind} />
                              <RetriesBadge count={totalRetries} />
                              <EscalationBadge row={r} />
                              <DepartureBadge iso={r.departure_date} />
                            </div>
                            {/* Sub line: customer + refs */}
                            <div className="mt-1 flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs text-white/55 flex-wrap font-normal min-w-0">
                              <UserIcon className="w-3 h-3 text-white/40 shrink-0" />
                              <span className="text-white/80 truncate max-w-[12rem] sm:max-w-none">
                                {r.user_name || r.user_email || 'Unknown user'}
                              </span>
                              <VipBadge userId={r.user_id || null} email={r.user_email || null} />
                              {r.user_email && r.user_name && (
                                <>
                                  <span className="text-white/30 hidden sm:inline">·</span>
                                  <span className="truncate max-w-[12rem] sm:max-w-none">{r.user_email}</span>
                                </>
                              )}
                              {r.booking_reference && (
                                <>
                                  <span className="text-white/30">·</span>
                                  <span className="inline-flex items-center gap-1">
                                    <span className="text-[10px] uppercase tracking-wider text-white/35">
                                      Ref
                                    </span>
                                    <span className="font-mono text-white/70 select-all">
                                      {r.booking_reference}
                                    </span>
                                  </span>
                                </>
                              )}
                              {r.confirmation_number && (
                                <>
                                  <span className="text-white/30">·</span>
                                  <span className="inline-flex items-center gap-1">
                                    <span className="text-[10px] uppercase tracking-wider text-white/35">
                                      PNR
                                    </span>
                                    <span className="font-mono text-white/70 select-all">
                                      {r.confirmation_number}
                                    </span>
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Past-due amount + chevron — full-width row on
                              mobile (label/amount/chevron justified across the
                              line), pinned right column on sm+. */}
                          <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 pt-1.5 sm:pt-0 border-t sm:border-t-0 border-white/10 sm:border-0">
                            <div className="flex items-baseline sm:block gap-2 sm:gap-0 sm:text-right">
                              <div className="text-[10px] uppercase tracking-wider text-rose-300/80 font-normal">
                                Past-due
                              </div>
                              <div className="text-sm sm:text-base text-rose-200 font-normal tabular-nums">
                                {fmtCurrency(r.past_due_total_cents)}
                              </div>
                            </div>
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-white/40 shrink-0" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-white/40 shrink-0" />
                            )}
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="px-3 sm:px-4 py-3 sm:py-4 bg-black/30 border-t border-white/10 space-y-4">
                          {/* Payment schedule grid */}
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-white/40 font-normal mb-2 flex items-center gap-1.5">
                              <CreditCard className="w-3 h-3" />
                              Payment schedule
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 text-xs sm:text-sm font-normal">
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-white/40 truncate">
                                  Per payment
                                </div>
                                <div className="text-white tabular-nums truncate">
                                  {fmtCurrency(r.per_payment_cents)}
                                  {r.interval && (
                                    <span className="text-white/40 text-[10px] sm:text-xs ml-1">
                                      ({intervalLabel(r.interval, r.interval_count)})
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-white/40 truncate">
                                  Next attempt
                                </div>
                                <div className="text-white truncate">{fmtDate(r.next_payment_date)}</div>
                              </div>
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-white/40 truncate">
                                  Paid to date
                                </div>
                                <div className="text-white tabular-nums truncate">
                                  {fmtCurrency(r.paid_amount_cents)}
                                </div>
                              </div>
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-white/40 truncate">
                                  Remaining
                                </div>
                                <div className="text-white tabular-nums truncate">
                                  {r.remaining_amount_cents != null
                                    ? fmtCurrency(r.remaining_amount_cents)
                                    : '—'}
                                </div>
                              </div>
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-white/40 truncate">
                                  Total contracted
                                </div>
                                <div className="text-white tabular-nums truncate">
                                  {r.total_amount_cents != null
                                    ? fmtCurrency(r.total_amount_cents)
                                    : '—'}
                                </div>
                              </div>
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-white/40 truncate">
                                  Booking status
                                </div>
                                <div className="text-white truncate">{r.booking_status || '—'}</div>
                              </div>
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-white/40 truncate">
                                  Booked
                                </div>
                                <div className="text-white">
                                  {r.is_ticketed ? 'Yes' : 'No'}
                                </div>
                              </div>
                              <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-white/40 truncate">
                                  Cancels at
                                </div>
                                <div className="text-white truncate">{fmtDate(r.cancel_at)}</div>
                              </div>
                            </div>
                          </div>

                          {/* Open invoices */}
                          {Array.isArray(r.open_invoices) && r.open_invoices.length > 0 && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-white/40 font-normal mb-2">
                                {r._payment_processor === 'nmi'
                                  ? `Past-due installments (${r.open_invoices.length})`
                                  : `Open invoices (${r.open_invoices.length})`}
                              </div>
                              <div className="rounded-xl border border-white/10 divide-y divide-white/10 overflow-hidden">
                                {r.open_invoices.map((inv) => {
                                  const dueMs = inv.due_date
                                    ? new Date(inv.due_date).getTime()
                                    : null;
                                  const overdueDays =
                                    dueMs != null
                                      ? Math.max(0, Math.floor((Date.now() - dueMs) / 86400000))
                                      : null;
                                  return (
                                    <div
                                      key={inv.id}
                                      className="px-3 py-2 flex items-start justify-between gap-2 sm:gap-3 bg-white/[0.02]"
                                    >
                                      <div className="min-w-0 flex-1">
                                        <div className="text-sm text-white font-normal tabular-nums flex flex-wrap items-baseline gap-x-2">
                                          <span>{fmtCurrency(inv.amount_due_cents)}</span>
                                          {inv.amount_remaining_cents !== inv.amount_due_cents && (
                                            <span className="text-white/40 text-xs">
                                              ({fmtCurrency(inv.amount_remaining_cents)} remaining)
                                            </span>
                                          )}
                                        </div>
                                        {/* Metadata line: flex-wrap so all the
                                            middot-separated chips reflow onto
                                            new lines on narrow screens instead
                                            of overflowing the card. */}
                                        <div className="text-[11px] text-white/50 mt-0.5 font-normal flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                                          <span>Due {fmtDate(inv.due_date)}</span>
                                          {overdueDays != null && overdueDays > 0 && (
                                            <>
                                              <span className="text-white/30">·</span>
                                              <span className="text-rose-300">{overdueDays}d overdue</span>
                                            </>
                                          )}
                                          <span className="text-white/30">·</span>
                                          <span>
                                            {inv.attempt_count || 0} attempt{inv.attempt_count === 1 ? '' : 's'}
                                          </span>
                                          {inv.next_payment_attempt && (
                                            <>
                                              <span className="text-white/30">·</span>
                                              <span>Retry {fmtDateTime(inv.next_payment_attempt)}</span>
                                            </>
                                          )}
                                          {inv.number && (
                                            <>
                                              <span className="text-white/30">·</span>
                                              <span className="font-mono break-all">{inv.number}</span>
                                            </>
                                          )}
                                          {inv._nmi_status && (
                                            <>
                                              <span className="text-white/30">·</span>
                                              <span className="uppercase tracking-wider text-[10px] text-rose-300/80">
                                                {inv._nmi_status}
                                              </span>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                      {inv.hosted_invoice_url && (
                                        <a
                                          href={inv.hosted_invoice_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-[11px] sm:text-xs text-brand-300 hover:text-brand-200 inline-flex items-center gap-1 shrink-0 whitespace-nowrap"
                                        >
                                          <span className="hidden sm:inline">View invoice</span>
                                          <span className="sm:hidden">View</span>
                                          <ExternalLink className="w-3 h-3" />
                                        </a>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* IDs + quick actions. `break-all` lets Stripe's
                              long sub_/cus_ IDs wrap inside narrow viewports
                              instead of forcing horizontal scroll on the whole
                              card. */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] text-white/40 font-normal min-w-0">
                            {r.stripe_subscription_id && (
                              <div className="min-w-0">
                                <span className="uppercase tracking-wider text-white/30 mr-1">Sub</span>
                                <span className="font-mono text-white/70 select-all break-all">
                                  {r.stripe_subscription_id}
                                </span>
                              </div>
                            )}
                            {r.stripe_customer_id && (
                              <div className="min-w-0">
                                <span className="uppercase tracking-wider text-white/30 mr-1">Cus</span>
                                <span className="font-mono text-white/70 select-all break-all">
                                  {r.stripe_customer_id}
                                </span>
                              </div>
                            )}
                            {r._payment_processor === 'nmi' && (
                              <div className="min-w-0">
                                <span className="uppercase tracking-wider text-white/30 mr-1">Processor</span>
                                <span className="text-white/70">NMI (Defera Capital)</span>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2 pt-1">
                            {r.user_id && (
                              <Link
                                to={`/UserDetails?userId=${encodeURIComponent(r.user_id)}`}
                                className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-white/80 bg-white/10 hover:bg-white/15 border border-white/15 px-2.5 sm:px-3 py-1.5 rounded-full transition-colors"
                              >
                                <UserIcon className="w-3 h-3" />
                                View user
                              </Link>
                            )}
                            {r.user_email && (
                              <a
                                href={`mailto:${r.user_email}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-white/80 bg-white/10 hover:bg-white/15 border border-white/15 px-2.5 sm:px-3 py-1.5 rounded-full transition-colors"
                              >
                                <Mail className="w-3 h-3" />
                                <span className="hidden sm:inline">Email customer</span>
                                <span className="sm:hidden">Email</span>
                              </a>
                            )}
                            {r.stripe_subscription_id && (
                              <a
                                href={`https://dashboard.stripe.com/subscriptions/${r.stripe_subscription_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-white/80 bg-white/10 hover:bg-white/15 border border-white/15 px-2.5 sm:px-3 py-1.5 rounded-full transition-colors"
                              >
                                <ExternalLink className="w-3 h-3" />
                                <span className="hidden sm:inline">Open in Stripe</span>
                                <span className="sm:hidden">Stripe</span>
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
